import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Alert,
  Col,
  Row
} from 'reactstrap';
import Select from 'react-select';
import {
  useGetCountryListQuery,
  useAddActionNoteMutation,
  useUpdateUserDetailsMutation
} from '../../../../store/api/userManagementApi.js';
import AddCommentModal from './add_note.model.jsx';

const UserUpdateModal = ({ isOpen, toggle, userData, onUpdate }) => {
  // Initial form state
  const [countryList, setCountryList] = useState([]);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    status: userData?.status || 'active',
    phone:userData.phone || '',
    user_id:userData.user_id,
    country:userData.country_code, 
    password:userData.password,

  });
  const {
    data: countriesResponse
  } = useGetCountryListQuery('user_dropdown');
  const [addActionNote] = useAddActionNoteMutation();
  const [updateUserDetails] = useUpdateUserDetailsMutation();

  useEffect(() => {
    if (userData) {
      setFormData({
        first_name: userData.first_name || '',
        last_name: userData.last_name || '',
        email: userData.user_email || '',
        status: userData.status || 'active',
        phone: userData.phone || '',
        user_id: userData.user_id,
        country: userData.country_code,
        password: userData.password,
      });
    }
  }, [userData]);

  useEffect(() => {
    const responseData = countriesResponse?.data || [];
    const mappedCountries = responseData.map((element, index) => ({
      value: index + 1,
      label: element.country_name,
      code: element.code
    }));
    setCountryList(mappedCountries);
  }, [countriesResponse]);


  // Form validation state
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
  const [pendingUpdateData, setPendingUpdateData] = useState(null);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

    // Handle input changes
  const handleChangeCountry = code => {
    setFormData(prev => ({
      ...prev,
      country: code
    }));
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError(null);

    if (!validateForm()) {
      return;
    }

    // Store the data and show note modal
    setPendingUpdateData(formData);
    setIsCommentModalOpen(true);
  };

  // Handle note submission
  const handleCommentSubmit = async (note) => {
    try {
      const commentData = {
        action_type: 'user-update',
        note: note,
        user_id: formData.user_id
      };
      
      // Submit note (success or failure doesn't matter for main flow)
      await addActionNote(commentData).unwrap();
      
      // Close note modal and proceed with original update
      setIsCommentModalOpen(false);
      if (pendingUpdateData) {
        proceedWithUpdate(pendingUpdateData);
      }
    } catch (error) {
      console.error('Comment submission error:', error);
      // Still proceed with update even if note fails
      setIsCommentModalOpen(false);
      if (pendingUpdateData) {
        proceedWithUpdate(pendingUpdateData);
      }
    }
  };

  // Proceed with the actual user update
  const proceedWithUpdate = async (updateData) => {
    try {
      setLoading(true);
      
      const response = await updateUserDetails(updateData).unwrap();
      
      if (response.success) {
        onUpdate(updateData);
        toggle();
      } else {
        setApiError(response.message || 'Failed to update user');
      }
    } catch (err) {
      setApiError('An error occurred while updating user details');
      console.error('Update error:', err);
    } finally {
      setLoading(false);
      setPendingUpdateData(null);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <Form onSubmit={handleSubmit}>
        <ModalHeader toggle={toggle}>
          <span className="fw-bold">Edit User Details</span>
        </ModalHeader>
        
        <ModalBody>
          {apiError && (
            <Alert color="danger" className="mb-4">
              {apiError}
            </Alert>
          )}
          
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label for="first_name" className="fw-bold">
                  First Name
                </Label>
                <Input
                  id="first_name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  placeholder="Enter first name"
                  invalid={!!errors.first_name}
                />
                {errors.first_name && (
                  <div className="text-danger small mt-1">
                    {errors.first_name}
                  </div>
                )}
              </FormGroup>
            </Col>
            
            <Col md={6}>
              <FormGroup>
                <Label for="last_name" className="fw-bold">
                  Last Name
                </Label>
                <Input
                  id="last_name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  placeholder="Enter last name"
                  invalid={!!errors.last_name}
                />
                {errors.last_name && (
                  <div className="text-danger small mt-1">
                    {errors.last_name}
                  </div>
                )}
              </FormGroup>
            </Col>
          </Row>
          
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label for="email" className="fw-bold">
                  Email
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter email address"
                  invalid={!!errors.email}
                />
                {errors.email && (
                  <div className="text-danger small mt-1">
                    {errors.email}
                  </div>
                )}
              </FormGroup>
            </Col>
            
            <Col md={6}>
              <FormGroup>
              <Label for="phone" className="fw-bold">
                  Phone
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Enter phone address"
                  invalid={!!errors.phone}
                />
                {errors.phone && (
                  <div className="text-danger small mt-1">
                    {errors.phone}
                  </div>
                )}
              </FormGroup>
            </Col>

            <Col md={6}>
              <FormGroup>
              <Label for="password" className="fw-bold">
                  Password
                </Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter password"
                  invalid={!!errors.password}
                />
                {errors.password && (
                  <div className="text-danger small mt-1">
                    {errors.password}
                  </div>
                )}
              </FormGroup>
            </Col>
            <Col md={6}>
                <Label for="exampleName">
                  Select country
                </Label>

                <Select
                  value={countryList.find(country => country.code === formData.country)}
                  name="country"
                  onChange={(value) => {
                    handleChangeCountry(value.code, "country");
                  }}
                  classNamePrefix="react-select"
                  options={countryList}
                />

            </Col>
           
          </Row>
        </ModalBody>
        
        <ModalFooter>
          <Button 
            type="button" 
            color="secondary" 
            onClick={toggle}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            color="primary"
            disabled={loading}
          >
            {loading ? (
              <span className="d-flex align-items-center">
                {/* <Spinner size="sm" className="me-2" /> */}
                Updating...
              </span>
            ) : (
              'Save'
            )}
          </Button>
        </ModalFooter>
      </Form>
      
      <AddCommentModal
        isOpen={isCommentModalOpen}
        toggle={() => setIsCommentModalOpen(false)}
        onSubmit={handleCommentSubmit}
        actionType="user-update"
      />
    </Modal>
  );
};

export default UserUpdateModal;